/**
 * Analytics — Phase 2 no-op shim.
 *
 * Phase 2 ships at /v2/onboarding as a preview route without backend
 * wiring. This module exposes a `track()` no-op so step components
 * can emit events at the right places NOW; Phase 7 cutover (or
 * earlier when we pick a real analytics provider) can swap the
 * implementation in a single file.
 *
 * Why no-op now instead of deferred entirely: instrumenting late
 * means digging through every step component again. Stubbing now
 * reduces Phase 7 wiring to one-line-per-call.
 *
 * Intended events (informational — not enforced by types):
 *   - 'onboarding:step'        { n: number, journey?: JourneyId }
 *   - 'onboarding:journey_set' { journey: JourneyId }
 *   - 'onboarding:complete'    { journey: JourneyId, skipped: string[] }
 */

export type AnalyticsEvent = string;
export type AnalyticsProperties = Record<string, unknown>;

let provider: ((event: AnalyticsEvent, props?: AnalyticsProperties) => void) | null = null;

/** Swap the impl. Phase 7 wires this to a real analytics SDK. */
export function setAnalyticsProvider(
  fn: ((event: AnalyticsEvent, props?: AnalyticsProperties) => void) | null,
): void {
  provider = fn;
}

/** Fire an analytics event. No-op if no provider is installed. */
export function track(event: AnalyticsEvent, props?: AnalyticsProperties): void {
  if (provider) {
    try {
      provider(event, props);
    } catch (err) {
      // Never let analytics throw into product code.
      // eslint-disable-next-line no-console
      console.warn('[analytics] provider threw:', err);
    }
    return;
  }
  // Dev-only debug: log the event so we can see them flowing during
  // local development. Suppress in production builds and in tests
  // (vitest sets MODE='test'). Uses Vite's native env API instead of
  // Node's `process` global so `tsc --noEmit` doesn't require
  // `@types/node` (which is intentionally NOT a devDep — this is a
  // browser app, not Node).
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    // eslint-disable-next-line no-console
    console.debug('[track]', event, props ?? {});
  }
}
